import React, { useState, useEffect, useMemo } from 'react';
import { useVideoGenerationQueue } from '../hooks/useVideoGenerationQueue';
import { useNavigate } from 'react-router-dom';
import { authenticatedFetch } from '../utils/apiUtils';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, Minimize2, X, Maximize2, CheckCircle2, AlertCircle } from 'lucide-react';

/**
 * Global modal notification component for video generation progress
 * Shows a centered modal that can be minimized to the top right
 */
const VideoGenerationNotification: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { queue, removeFromQueue, updateQueueItem } = useVideoGenerationQueue();
  const [isVisible, setIsVisible] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [dismissedCompleted, setDismissedCompleted] = useState<Set<string>>(new Set());

  // Get active items (queued or processing)
  const activeItems = useMemo(() => {
    return queue.filter(
      item => item.status === 'queued' || item.status === 'processing'
    );
  }, [queue]);

  // Get recently completed items (completed or failed)
  const completedItems = useMemo(() => {
    return queue.filter(
      item => (item.status === 'completed' || item.status === 'failed') && !dismissedCompleted.has(item.id)
    );
  }, [queue, dismissedCompleted]);

  // Show if we have active items or undismiised completed items
  useEffect(() => {
    const hasActive = activeItems.length > 0;
    const hasCompleted = completedItems.length > 0;

    // Automatically open (un-minimize) when a new generation starts if previously closed
    if (activeItems.length > 0 && !isVisible) {
      setIsVisible(true);
      setIsMinimized(false);
    } else if (activeItems.length === 0 && hasCompleted && !isVisible) {
      // Show completed state if not visible
      setIsVisible(true);
      // Keep minimized if it was minimized, otherwise centered
    } else if (activeItems.length === 0 && !hasCompleted) {
      // Hide if nothing to show
      setIsVisible(false);
    }
  }, [activeItems.length, completedItems.length]);

  // Polling for active items status
  useEffect(() => {
    // only poll if there are active items and we have a user ID
    if (activeItems.length === 0 || !user?.id) return;

    const pollStatus = async () => {
      try {
        // Fetch recent generations to check status
        const response = await authenticatedFetch(`/api/v1/user/generations?limit=20&userId=${user.id}`);

        if (!response.ok) return;

        const result = await response.json();

        if (result.success && result.data.generations) {
          const generations = result.data.generations;

          // Check status of each active item
          activeItems.forEach(item => {
            const generation = generations.find((g: any) => g.id === item.generationId);

            if (generation) {
              if (generation.status === 'completed') {
                updateQueueItem(item.id, {
                  status: 'completed',
                  completedAt: Date.now()
                });
              } else if (generation.status === 'failed') {
                updateQueueItem(item.id, {
                  status: 'failed',
                  error: generation.error_message || 'Generation failed'
                });
              }
            }
          });
        }
      } catch (error) {
        console.error('Error polling video status:', error);
      }
    };

    // Poll every 5 seconds
    const intervalId = setInterval(pollStatus, 5000);

    return () => clearInterval(intervalId);
  }, [activeItems, updateQueueItem, user?.id]);

  if (!isVisible) return null;

  // Render items based on status
  // If active items exist, show them. Otherwise show completed items.
  const isProcessing = activeItems.length > 0;
  const itemsToShow = isProcessing ? activeItems : completedItems;
  const currentItem = itemsToShow[0]; // Show the first item

  if (!currentItem) return null;

  // Use the imageUrl from queue item, or fallback if needed
  const previewImage = currentItem.imageUrl;

  const handleDismiss = () => {
    if (isProcessing) {
      // If processing, just minimize
      setIsMinimized(true);
    } else {
      // If completed, dismiss
      setDismissedCompleted(prev => {
        const newSet = new Set(prev);
        newSet.add(currentItem.id);
        return newSet;
      });
      setIsVisible(false);
    }
  };

  // Minimized View (Top Right Toast)
  if (isMinimized) {
    return (
      <div
        className="fixed top-24 right-6 z-50 animate-fade-in-up cursor-pointer transition-transform hover:scale-105"
        onClick={() => setIsMinimized(false)}
      >
        <div className="bg-gray-900 border border-gray-700 rounded-lg shadow-2xl p-4 w-80 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {isProcessing ? (
              <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
            ) : currentItem.status === 'failed' ? (
              <AlertCircle className="w-5 h-5 text-red-500" />
            ) : (
              <CheckCircle2 className="w-5 h-5 text-green-500" />
            )}
            <div className="text-sm">
              <p className="font-semibold text-white">
                {isProcessing
                  ? `Generating Video (${activeItems.length})`
                  : currentItem.status === 'failed' ? 'Generation Failed' : 'Video Ready!'}
              </p>
              <p className="text-gray-400 text-xs text-left">
                {isProcessing ? 'Processing in background...' : 'Click to open'}
              </p>
            </div>
          </div>
          <button
            className="p-1 hover:bg-gray-800 rounded text-gray-400 hover:text-white transition-colors"
          >
            <Maximize2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // Maximized View (Centered Modal)
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md overflow-hidden border border-gray-200 dark:border-gray-700 transform transition-all animate-scale-in">
        {/* Header */}
        <div className="bg-gray-50 dark:bg-gray-900/50 px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="font-semibold text-lg text-gray-900 dark:text-white flex items-center">
            {isProcessing ? (
              <>
                <Loader2 className="w-5 h-5 text-blue-500 animate-spin mr-2" />
                Generating Video...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-5 h-5 text-green-500 mr-2" />
                Generation Complete
              </>
            )}
          </h3>
          <div className="flex items-center space-x-2">
            {isProcessing && (
              <button
                onClick={() => setIsMinimized(true)}
                className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg text-gray-500 dark:text-gray-400 transition-colors"
                title="Minimize"
              >
                <Minimize2 className="w-5 h-5" />
              </button>
            )}
            {!isProcessing && (
              <button
                onClick={handleDismiss}
                className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg text-gray-500 dark:text-gray-400 transition-colors"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="relative w-full aspect-video bg-gray-100 dark:bg-gray-900 rounded-lg overflow-hidden mb-4 border border-gray-200 dark:border-gray-700">
            {previewImage && (
              <img
                src={previewImage}
                alt="Preview"
                className="w-full h-full object-cover opacity-80"
              />
            )}

            {isProcessing && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/30 backdrop-blur-[2px]">
                <div className="w-full max-w-[200px] space-y-2">
                  <div className="h-1.5 w-full bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 animate-progress-indeterminate"></div>
                  </div>
                  <p className="text-white text-xs text-center font-medium shadow-sm">AI is animating your scene...</p>
                </div>
              </div>
            )}
          </div>

          <p className="text-sm text-gray-600 dark:text-gray-300 text-center mb-4">
            {isProcessing
              ? "You can minimize this window and continue working. We'll notify you when it's ready."
              : "Your video has been generated successfully! You can find it in your Recent Generations."}
          </p>

          {!isProcessing && (
            <button
              onClick={() => {
                handleDismiss();
                // Navigate to smart effects if not already there, or scroll to generations
                if (!window.location.pathname.includes('smart-effects')) {
                  navigate('/smart-effects');
                }
              }}
              className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center"
            >
              View Result
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoGenerationNotification;
